import {noop} from 'lodash';
import React from 'react';

import {sanitizeHtml} from '../../../../../../libs/shared/core/src/helpers/sanitization/html';
import {InboundOptimisticMessage} from '../../../../../../libs/shared/core/src/models/api/chatDirectPushMessageModel';
import {MessageTypesEnum} from '../../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {
  buildContactUid,
  ContactSourcesEnum,
} from '../../../../../../libs/shared/core/src/models/api/contactModel';
import {
  MessageModel,
  MessageStatusesEnum,
  parseMessage,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../../libs/shared/core/src/models/api/recipientModel';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {makeGetCardForHandle} from '../../../../../store/data/cards/cardsSelectors';
import {getContacts} from '../../../../../store/data/dataSelectors';
import {MessageViewerBase} from '../message/messageViewerBase';
import {MessageViewerSynchronous} from '../message/synchronous/messageViewerSynchronous';

/*
 * Props.
 */

export interface ChatDirectPushMessageProps {
  message: InboundOptimisticMessage;
  panelId: ViewerPanelId;
  conversationNamespace: string;
  conversationId: number;
  cellsContext: CellsContext;
  shouldShowAvatar?: boolean;
}

/*
 * Component.
 */

export const ChatDirectPushMessage = ({
  message,
  shouldShowAvatar = false,
  panelId,
  conversationNamespace,
  conversationId,
  cellsContext,
}: ChatDirectPushMessageProps) => {
  const {
    id,
    text,
    sender: {handle: senderHandle},
  } = message;

  const contact = useAppSelector((state) => {
    const contactUid = buildContactUid(ContactSourcesEnum.FRONT_CHAT, senderHandle);
    return getContacts(state)[contactUid];
  });
  const card = useAppSelector((state) => makeGetCardForHandle(senderHandle, conversationNamespace)(state));

  if (!contact) {
    return null;
  }

  const {uid, handle, cardIds, source} = contact;

  const visualContact = {
    contact: {
      uid,
      source,
      handle,
      cardIds,
    },
    namespace: conversationNamespace,
    card,
    name: card?.name,
  };

  return (
    <MessageViewerBase timelineVariation={TimelineVariationsEnum.SYNCHRONOUS} isChatFollowUp>
      <MessageViewerSynchronous
        message={computeDirectPushMessage({text, id, conversationId})}
        panelId={panelId}
        cellsContext={cellsContext}
        isChatFollowUp={!shouldShowAvatar}
        visualRecipients={{from: [{...visualContact, role: RecipientRolesEnum.FROM}]}}
        namespace={conversationNamespace}
        activitySourceVisualType={undefined}
        activityId={undefined}
        isChatUserOnline
        isMessageViewerExpanded
        isNewUnread
        isUserAuthor
        isFirstMessage
        isImageMessage={false}
        needsSeenPill={false}
        hasMessageAnnotations={false}
        onReplyAllClick={noop}
        onReplyClick={noop}
        onForwardClick={noop}
        onCloneClick={noop}
        onDestroyConversationClick={noop}
        onPopoutClick={noop}
      />
    </MessageViewerBase>
  );
};

/*
 * Helpers.
 */

function computeDirectPushMessage({
  text,
  id,
  conversationId,
}: {
  text: string;
  id: string;
  conversationId: number;
}): MessageModel {
  return parseMessage({
    id: 1,
    uid: id,
    text,
    date: Date.now(),
    created_at: Date.now(),
    type: MessageTypesEnum.FRONT_CHAT,
    blurb: text,
    body: sanitizeHtml(text),
    status: MessageStatusesEnum.IMPORTED,
    conversation_id: conversationId,
    conversation_url: '',
    inbound: true,
    url: '',
    in_conversation_url: '',
    updated_at: Date.now(),
    content_loaded: true,
    render_full: true,
    attachments: [],
    headers: {
      layer_page_url: '',
      front_chat_page_url: '',
      qual_bot_responses: [],
    },
  });
}
